import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const JASSO_LOGIN_URL = process.env.REACT_APP_JBM_LOGIN_URL;

export const Login = () => {
  const navigate = useNavigate();

  // step: "email" | "method" | "mail-sent" | "email-otp" | "totp-otp" | "totp-setup"
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");           // TOTP code
  const [emailOtp, setEmailOtp] = useState(""); // emailed enrollment code
  const [qrCode, setQrCode] = useState(null);
  const [hasSecret, setHasSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [notAuthorized, setNotAuthorized] = useState(false);
  const cooldownRef = useRef(null);

  /* ─────────────────────── helpers ─────────────────────── */
  const clearMessages = () => { setError(""); setInfo(""); };

  const startResendCooldown = () => {
    setResendCooldown(20);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

  const goBack = (target) => () => {
    clearMessages();
    setOtp("");
    setEmailOtp("");
    setStep(target);
  };

  const headers = { "Content-Type": "application/json" };

  /* ── step 1: email → eligibility check ── */
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const res = await fetch(`${API}/API/V2/auth/login/status`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.code === "C00" && data.eligible) {
        setHasSecret(!!data.hasSecret);
        setStep("method");
      } else {
        // Not an alternative-login user — show a "not authorised" popup.
        setNotAuthorized(true);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  /* ── step 2a: mail link ── */
  const handleSendMailLink = async () => {
    setLoading(true);
    clearMessages();
    try {
      const res = await fetch(`${API}/API/V2/auth/login/mail`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.code === "C00") {
        setInfo("Login link sent to your email. Please check your inbox and click the link to sign in.");
        setStep("mail-sent");
        startResendCooldown();
      } else {
        setError(data.message || "Failed to send login link. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  /* ── step 2b: authenticator — route by whether a secret already exists ── */
  const handleAuthenticatorChoice = () => {
    clearMessages();
    if (hasSecret) {
      setStep("totp-otp");           // already enrolled → enter TOTP code
    } else {
      handleSendEmailOtp();          // enroll → verify email first
    }
  };

  const handleSendEmailOtp = async () => {
    setLoading(true);
    clearMessages();
    try {
      const res = await fetch(`${API}/API/V2/auth/login/totp/send-otp`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.code === "C00") {
        setInfo("We've emailed you a 6-digit verification code.");
        setStep("email-otp");
        startResendCooldown();
      } else {
        setError(data.message || "Failed to send verification code. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearMessages();
    try {
      const res = await fetch(`${API}/API/V2/auth/login/totp/verify-otp`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email, otp: emailOtp }),
      });
      const data = await res.json();
      if (data.code === "C00") {
        setQrCode(data.qrCode);
        setEmailOtp("");
        setStep("totp-setup");
      } else {
        setError(data.message || "Invalid verification code. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const handleVerifyTOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearMessages();
    try {
      const res = await fetch(`${API}/API/V2/auth/login/totp/login`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email, otp }),
      });
      const data = await res.json();
      if (data.code === "C00") {
        localStorage.setItem("jassoVerified", false);
        navigate(`/auth/verify/${data.token}`);
      } else {
        setError(data.message || "Invalid code. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  /* ─────────────────────── render ────────────────────────── */
  return (
    <div className="min-h-screen flex items-center justify-center bg-violet-50 px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header bar */}
        <div className="bg-jiojbm60 px-8 py-5">
          <h1 className="text-white text-xl font-bold tracking-tight">JBM Portal</h1>
          <p className="text-violet-200 text-xs mt-0.5">Sign in to your account</p>
        </div>

        <div className="px-8 py-8 space-y-6">
          {/* Error / Info banners */}
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              <span className="mt-px">⚠</span>
              <span>{error}</span>
            </div>
          )}
          {info && (
            <div className="flex items-start gap-2 px-4 py-3 bg-green-50 text-green-700 text-sm rounded-lg border border-green-200">
              <span className="mt-px">✓</span>
              <span>{info}</span>
            </div>
          )}

          {/* ── Step: Email ── */}
          {step === "email" && (
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-jiojbm40 focus:border-transparent transition"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                {loading ? "Checking…" : "Continue"}
              </button>
            </form>
          )}

          {/* ── Step: Choose login method ── */}
          {step === "method" && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 text-center">
                Signing in as{" "}
                <span className="font-semibold text-gray-800">{email}</span>
              </p>

              <button
                onClick={handleSendMailLink}
                disabled={loading}
                className="w-full flex items-center gap-4 border-2 border-gray-200 hover:border-jiojbm40 hover:bg-violet-50 rounded-xl px-5 py-4 transition-all text-left disabled:opacity-50"
              >
                <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-jiojbm60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-gray-800 text-sm">Login via Mail</div>
                  <div className="text-xs text-gray-500 mt-0.5">Receive a sign-in link on your email</div>
                </div>
              </button>

              <button
                onClick={handleAuthenticatorChoice}
                disabled={loading}
                className="w-full flex items-center gap-4 border-2 border-gray-200 hover:border-jiojbm40 hover:bg-violet-50 rounded-xl px-5 py-4 transition-all text-left disabled:opacity-50"
              >
                <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-jiojbm60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-gray-800 text-sm">Login via Authenticator</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {hasSecret ? "Enter the code from your app" : "Set up Google Authenticator or similar"}
                  </div>
                </div>
              </button>

              <button
                onClick={goBack("email")}
                className="w-full text-sm text-gray-400 hover:text-gray-600 pt-1 transition-colors"
              >
                ← Change email
              </button>
            </div>
          )}

          {/* ── Step: Mail link sent confirmation ── */}
          {step === "mail-sent" && (
            <div className="space-y-5 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Check your email</p>
                <p className="text-xs text-gray-500 mt-1">
                  A login link has been sent to{" "}
                  <span className="font-medium text-gray-700">{email}</span>.
                  Click it to sign in.
                </p>
              </div>
              <button
                onClick={handleSendMailLink}
                disabled={loading || resendCooldown > 0}
                className="text-xs text-jiojbm60 hover:text-jiojbm70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Sending…" : resendCooldown > 0 ? `Resend link (${resendCooldown}s)` : "Resend link"}
              </button>
              <button onClick={goBack("method")} className="block w-full text-sm text-gray-400 hover:text-gray-600 transition-colors">
                ← Back
              </button>
            </div>
          )}

          {/* ── Step: Email OTP (verify before TOTP setup) ── */}
          {step === "email-otp" && (
            <form onSubmit={handleVerifyEmailOtp} className="space-y-5">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-7 h-7 text-jiojbm60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-600">
                  Enter the 6-digit code we emailed to{" "}
                  <span className="font-medium text-gray-700">{email}</span> to verify it's you.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Verification Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  autoFocus
                  value={emailOtp}
                  onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="• • • • • •"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-xl font-mono tracking-[0.6em] [text-indent:0.6em] text-center focus:outline-none focus:ring-2 focus:ring-jiojbm40 transition"
                />
              </div>
              <button
                type="submit"
                disabled={loading || emailOtp.length !== 6}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                {loading ? "Verifying…" : "Verify & Continue"}
              </button>
              <button
                type="button"
                onClick={handleSendEmailOtp}
                disabled={loading || resendCooldown > 0}
                className="block w-full text-xs text-jiojbm60 hover:text-jiojbm70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : "Resend code"}
              </button>
              <button type="button" onClick={goBack("method")} className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors">
                ← Back
              </button>
            </form>
          )}

          {/* ── Step: TOTP OTP entry (already set up) ── */}
          {step === "totp-otp" && (
            <form onSubmit={handleVerifyTOTP} className="space-y-5">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-7 h-7 text-jiojbm60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-600">
                  Open your authenticator app and enter the current 6-digit code.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Authenticator Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="• • • • • •"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-xl font-mono tracking-[0.6em] [text-indent:0.6em] text-center focus:outline-none focus:ring-2 focus:ring-jiojbm40 transition"
                />
              </div>
              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                {loading ? "Verifying…" : "Sign In"}
              </button>
              <button type="button" onClick={goBack("method")} className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors">
                ← Back
              </button>
            </form>
          )}

          {/* ── Step: TOTP setup — show QR then OTP entry ── */}
          {step === "totp-setup" && (
            <form onSubmit={handleVerifyTOTP} className="space-y-5">
              <div className="text-center space-y-3">
                <p className="text-sm font-medium text-gray-700">
                  Scan this QR code with your authenticator app
                </p>
                <p className="text-xs text-gray-500">
                  Use Google Authenticator, Microsoft Authenticator, or any TOTP app.
                </p>
                {qrCode && (
                  <div className="flex justify-center py-2">
                    <img
                      src={qrCode}
                      alt="Scan to set up authenticator"
                      className="w-48 h-48 rounded-xl border-2 border-gray-200 shadow-sm"
                    />
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100 pt-5">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Enter the 6-digit code from your app to verify
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="• • • • • •"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-xl font-mono tracking-[0.6em] [text-indent:0.6em] text-center focus:outline-none focus:ring-2 focus:ring-jiojbm40 transition"
                />
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                {loading ? "Verifying…" : "Verify & Sign In"}
              </button>

              <button
                type="button"
                onClick={goBack("method")}
                className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                ← Back
              </button>
            </form>
          )}
        </div>
      </div>

      {notAuthorized && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="px-6 py-6 text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                </svg>
              </div>
              <div>
                <p className="text-base font-bold text-gray-800">Not Authorised</p>
                <p className="text-sm text-gray-500 mt-1">
                  You are not authorised to use this login. Please contact your administrator.
                </p>
              </div>
              <button
                onClick={() => setNotAuthorized(false)}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                OK
              </button>
              <button
                onClick={() => { window.location.href = JASSO_LOGIN_URL; }}
                className="block w-full text-xs text-jiojbm60 hover:text-jiojbm70 transition-colors"
              >
                Go to primary login →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
